<script setup>
import { ref, reactive, computed, onMounted, onActivated } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';

import { useBase, data, hora } from './base';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import SandboxAPI from 'dashboard/api/fandata/sandbox';

// T-186, tela 3b — uma versão antiga em SÓ LEITURA.
//
// Duas perguntas, e a ordem entre elas é a decisão da tela: "o que fizemos naquele dia?" vem antes
// de "o que o Bira sabia?". Por isso o padrão é mostrar só o que mudou, e ver as 334 é a exceção:
// quem abre uma versão quase sempre quer entender a decisão, não reler a base inteira.
const { t } = useI18n();
const route = useRoute();
const { irPara } = useBase();

const carregando = ref(true);
const restaurando = ref(false);
const versao = reactive({});
const mudancas = reactive({ entraram: [], mudaram: [], sairam: [], total: 0 });
const respostas = ref([]);
const modo = ref('mudou');

const confirmandoRestauracao = ref(false);

const lista = computed(() => {
  if (modo.value === 'todas') {
    return respostas.value.map(r => ({
      ...r,
      etiqueta: null,
      chave: `t-${r.numero_card}`,
    }));
  }
  return [
    ...mudancas.entraram.map(m => ({ ...m, etiqueta: 'entrou' })),
    ...mudancas.mudaram.map(m => ({ ...m, etiqueta: 'mudou' })),
    ...mudancas.sairam.map(m => ({ ...m, etiqueta: 'saiu' })),
  ].map((item, i) => ({ ...item, chave: `m-${i}` }));
});

const ETIQUETAS = {
  entrou: 'bg-n-teal-3 text-n-teal-11',
  mudou: 'bg-n-amber-3 text-n-amber-11',
  saiu: 'bg-n-ruby-3 text-n-ruby-11',
};

const carregar = async () => {
  carregando.value = true;
  try {
    const { data } = await SandboxAPI.versao(route.params.id);
    Object.assign(versao, data.versao || {});
    Object.assign(mudancas, data.mudancas || {});
    respostas.value = data.respostas || [];
  } catch (error) {
    useAlert(t('BASE_VERSAO.ERRO'));
  } finally {
    carregando.value = false;
  }
};

// Restaurar deixou de ser tudo-ou-nada: dá para trazer só as respostas daquele dia ou só o jeito
// de falar. O caso que o clube descreveu é o segundo, "volta o tom de antes e mantém as respostas
// que a gente corrigiu depois", e ele custava uma semana de correção antes disto existir.
const escopo = ref('tudo');

const restaurar = async () => {
  restaurando.value = true;
  try {
    await SandboxAPI.restaurar(route.params.id, escopo.value);
    useAlert(t('BASE_VERSAO.RESTAURADA'));
    confirmandoRestauracao.value = false;
    irPara('capitao_base');
  } catch (error) {
    useAlert(t('BASE_VERSAO.ERRO_RESTAURAR'));
  } finally {
    restaurando.value = false;
  }
};

const voltar = () => irPara('capitao_base');

// As telas de Configurações vivem dentro de um <keep-alive>: sair e voltar NÃO remonta o
// componente, então `onMounted` não roda de novo e a tela volta com o estado de antes. Depois de
// publicar, isso significa uma tela achando que ainda existe um rascunho que já subiu, e todo
// salvamento batendo em 422. Recarregar ao reativar é o que mantém a tela e o servidor falando
// da mesma coisa.
let jaMontou = false;

onActivated(() => {
  if (!jaMontou) {
    jaMontou = true;
    return;
  }
  carregar();
});

onMounted(carregar);
</script>

<template>
  <SettingsLayout
    :is-loading="carregando"
    :loading-message="$t('BASE_VERSAO.HEADER')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="versao.rotulo || $t('BASE_VERSAO.HEADER')"
        :description="
          versao.publicada_em
            ? $t('BASE_VERSAO.META', {
                data: data(versao.publicada_em),
                hora: hora(versao.publicada_em),
                autor: versao.publicada_por || '—',
                respostas: versao.respostas,
              })
            : ''
        "
      >
        <template #actions>
          <div class="flex flex-wrap gap-2">
            <Button
              :label="$t('BASE_VERSAO.VOLTAR')"
              variant="faded"
              color="slate"
              size="sm"
              @click="voltar"
            />
            <Button
              :label="$t('BASE_VERSAO.RESTAURAR')"
              variant="faded"
              color="amber"
              size="sm"
              @click="confirmandoRestauracao = true"
            />
          </div>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-4 mt-4">
        <!-- Só leitura: dito uma vez, no topo, sem repetir em cada campo -->
        <p
          class="rounded-lg border border-n-weak bg-n-alpha-1 px-4 py-2 text-sm text-n-slate-11"
        >
          {{
            $t('BASE_VERSAO.SO_LEITURA', { data: data(versao.publicada_em) })
          }}
          <template v-if="versao.saiu_do_ar_em">
            {{ $t('BASE_VERSAO.PERIODO', { fim: data(versao.saiu_do_ar_em) }) }}
          </template>
        </p>

        <!-- Restaurar é destrutivo com o rascunho: a confirmação diz isso com todas as letras -->
        <div
          v-if="confirmandoRestauracao"
          class="rounded-lg border border-n-amber-6 bg-n-amber-2 px-4 py-3"
        >
          <p class="text-sm font-medium text-n-slate-12">
            {{ $t('BASE_VERSAO.CONFIRMA.TITULO') }}
          </p>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ $t('BASE_VERSAO.CONFIRMA.TEXTO') }}
          </p>
          <div class="flex gap-2 mt-3">
            <Button
              :label="$t('BASE_VERSAO.CONFIRMA.CANCELAR')"
              variant="faded"
              color="slate"
              size="sm"
              @click="confirmandoRestauracao = false"
            />
            <Button
              :label="$t('BASE_VERSAO.CONFIRMA.CONFIRMAR')"
              color="amber"
              size="sm"
              :is-loading="restaurando"
              @click="restaurar"
            />
          </div>
          <div class="flex flex-col gap-1.5 mt-1">
            <label
              v-for="opcao in ['tudo', 'respostas', 'comportamento']"
              :key="opcao"
              class="flex items-start gap-2 text-[12.5px] text-n-slate-11"
            >
              <input
                v-model="escopo"
                type="radio"
                :value="opcao"
                class="mt-0.5"
              />
              <span>
                {{ $t(`BASE_VERSAO.ESCOPO.${opcao.toUpperCase()}`) }}
              </span>
            </label>
          </div>
        </div>

        <!-- O resumo do dia -->
        <div class="flex flex-wrap items-center gap-4">
          <span class="text-sm text-n-teal-11">
            {{ $t('BASE_VERSAO.ENTRARAM', { n: mudancas.entraram.length }) }}
          </span>
          <span class="text-sm text-n-amber-11">
            {{ $t('BASE_VERSAO.MUDARAM', { n: mudancas.mudaram.length }) }}
          </span>
          <span class="text-sm text-n-ruby-11">
            {{ $t('BASE_VERSAO.SAIRAM', { n: mudancas.sairam.length }) }}
          </span>
          <div
            class="ml-auto flex rounded-lg border border-n-weak overflow-hidden"
          >
            <button
              type="button"
              class="px-3 py-1 text-sm"
              :class="
                modo === 'mudou'
                  ? 'bg-n-alpha-2 text-n-slate-12'
                  : 'text-n-slate-11'
              "
              @click="modo = 'mudou'"
            >
              {{ $t('BASE_VERSAO.SO_MUDOU') }}
            </button>
            <button
              type="button"
              class="px-3 py-1 text-sm border-l border-n-weak"
              :class="
                modo === 'todas'
                  ? 'bg-n-alpha-2 text-n-slate-12'
                  : 'text-n-slate-11'
              "
              @click="modo = 'todas'"
            >
              {{ $t('BASE_VERSAO.TODAS', { n: versao.respostas }) }}
            </button>
          </div>
        </div>

        <div v-if="lista.length" class="flex flex-col gap-2">
          <div
            v-for="item in lista"
            :key="item.chave"
            class="rounded-lg border border-n-weak px-4 py-3"
          >
            <div class="flex flex-wrap items-center gap-2">
              <span
                v-if="item.etiqueta"
                class="px-2 py-0.5 rounded text-[10px] uppercase tracking-wide"
                :class="ETIQUETAS[item.etiqueta]"
              >
                {{ $t(`BASE_VERSAO.ETIQUETA.${item.etiqueta.toUpperCase()}`) }}
              </span>
              <span class="text-sm text-n-slate-12">
                {{ item.titulo || item.pergunta }}
              </span>
              <span v-if="item.categoria" class="text-xs text-n-slate-11">
                · {{ item.categoria }}
              </span>
              <span v-if="item.numero" class="text-xs text-n-slate-11">
                · {{ item.numero }}
              </span>
            </div>
            <p class="mt-1 text-sm text-n-slate-11 whitespace-pre-line">
              {{ item.depois || item.texto }}
            </p>
            <p
              v-if="item.antes"
              class="mt-2 text-xs text-n-slate-11 border-l-2 border-n-weak pl-2 whitespace-pre-line"
            >
              {{ $t('BASE_VERSAO.ANTES') }} {{ item.antes }}
            </p>
            <p v-if="item.titulo_antes" class="mt-1 text-xs text-n-slate-11">
              {{ $t('BASE_VERSAO.CHAMAVA', { antes: item.titulo_antes }) }}
            </p>
          </div>
        </div>
        <p v-else class="text-sm text-n-slate-11 py-6">
          {{ $t('BASE_VERSAO.SEM_MUDANCAS') }}
        </p>
      </div>
    </template>
  </SettingsLayout>
</template>
